import React, { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import { useTranslation } from "react-i18next";
import Navbar from "components/navbar";
import {
  getSystemVehicleTypes,
  toggleSystemVehicleType,
  getVehicleTypeList,
  getVehicleCategoryList,
  getVehicleTypeCategories,
  updateVehicleTypeCategory,
  toggleCategoryActive,
  updateSystemVehicleType,
} from "services/customAPI";
import Loader from "components/loader/loader";
import { errorToast, successToast } from "utils/commons";
import { Switch } from "@chakra-ui/react";
import { Types } from "shared-lib";
import {
  MdOutlineFlightTakeoff,
  MdOutlineDirectionsCar,
  MdOutlineLocalShipping,
  MdOutlinePedalBike,
  MdOutlineDirectionsBus,
  MdOutlineDirectionsWalk,
  MdOutlineTwoWheeler,
} from "react-icons/md";
import { GiHelicopter, GiTruck } from "react-icons/gi";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";

type SystemVehicleType = {
  id: number;
  type: string;
  label: string;
  icon: string;
  active: boolean;
  categoriesCount: number;
  allowedTransportModes?: string[];
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  airplane: <MdOutlineFlightTakeoff className="h-8 w-8" />,
  helicopter: <GiHelicopter className="h-8 w-8" />,
  truck: <MdOutlineLocalShipping className="h-8 w-8" />,
  trailer: <GiTruck className="h-8 w-8" />,
  bus: <MdOutlineDirectionsBus className="h-8 w-8" />,
  car: <MdOutlineDirectionsCar className="h-8 w-8" />,
  motorcycle: <MdOutlineTwoWheeler className="h-8 w-8" />,
  bike: <MdOutlinePedalBike className="h-8 w-8" />,
  walking: <MdOutlineDirectionsWalk className="h-8 w-8" />,
};

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  airplane: { bg: "bg-sky-500/15", text: "text-sky-400", border: "border-sky-500/30" },
  helicopter: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
  truck: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
  trailer: { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  bus: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/30" },
  car: { bg: "bg-cyan-500/15", text: "text-cyan-400", border: "border-cyan-500/30" },
  motorcycle: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
  bike: { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" },
  walking: { bg: "bg-teal-500/15", text: "text-teal-400", border: "border-teal-500/30" },
};

function VehicleTypeList() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<SystemVehicleType[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<
    { id: number; name: string; active: boolean; transportMode?: string }[]
  >([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [togglingCatId, setTogglingCatId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSystemVehicleTypes();
      const payload = res?.data ?? res;
      setVehicleTypes(payload?.data ?? payload ?? []);
    } catch (error: any) {
      errorToast(t("Erro ao carregar tipos de veículos"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = useCallback(
    async (id: number) => {
      if (togglingId !== null) return; // prevent double-click
      setTogglingId(id);

      // Optimistic update — toggle immediately in UI
      const previous = vehicleTypes;
      const target = vehicleTypes.find((vt) => vt.id === id);
      const newActive = !target?.active;
      setVehicleTypes((prev) =>
        prev.map((vt) =>
          vt.id === id ? { ...vt, active: newActive } : vt
        )
      );

      try {
        await toggleSystemVehicleType(id);
        successToast(
          newActive
            ? `${target?.label} ativado com sucesso`
            : `${target?.label} desativado`
        );
      } catch {
        // Revert on error
        setVehicleTypes(previous);
        errorToast(t("Erro ao atualizar tipo de veículo"));
      } finally {
        setTogglingId(null);
      }
    },
    [t, togglingId, vehicleTypes]
  );

  const handleModeToggle = useCallback(
    async (id: number, currentModes: string[], modeToToggle: string) => {
      // Optimistic update
      const target = vehicleTypes.find((vt) => vt.id === id);
      if (!target) return;

      const previous = vehicleTypes;
      const isCurrentlyEnabled = currentModes.includes(modeToToggle);

      let nextModes = isCurrentlyEnabled
        ? currentModes.filter((m) => m !== modeToToggle)
        : [...currentModes, modeToToggle];

      // Prevent disabling completely (must have at least one mode)
      if (nextModes.length === 0) {
        errorToast(t("O veículo deve ter pelo menos um modo de transporte"));
        return;
      }

      setVehicleTypes((prev) =>
        prev.map((vt) =>
          vt.id === id ? { ...vt, allowedTransportModes: nextModes } : vt
        )
      );

      try {
        await updateSystemVehicleType(id, { allowedTransportModes: nextModes });
        successToast(t("Modo de transporte atualizado com sucesso"));
      } catch {
        setVehicleTypes(previous);
        errorToast(t("Erro ao atualizar modo de transporte"));
      }
    },
    [t, vehicleTypes]
  );

  const handleExpand = useCallback(
    async (id: number) => {
      if (expandedId === id) {
        setExpandedId(null);
        setExpandedCategories([]);
        return;
      }
      setExpandedId(id);
      setLoadingCategories(true);
      try {
        const res = await getVehicleTypeCategories(id);
        const payload = res?.data ?? res;
        setExpandedCategories(payload?.data ?? payload ?? []);
      } catch {
        setExpandedCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    },
    [expandedId]
  );

  const handleToggleCategory = useCallback(
    async (catId: number, currentActive: boolean) => {
      setTogglingCatId(catId);
      try {
        await toggleCategoryActive(catId, !currentActive);
        setExpandedCategories((prev) =>
          prev.map((c) =>
            c.id === catId ? { ...c, active: !currentActive } : c
          )
        );
        successToast(
          !currentActive ? t("Categoria ativada") : t("Categoria desativada")
        );
      } catch {
        errorToast(t("Erro ao atualizar categoria"));
      } finally {
        setTogglingCatId(null);
      }
    },
    [t]
  );

  if (loading) {
    return (
      <>
        <Navbar flag={false} brandText={t("Settings")} />
        <Loader />
      </>
    );
  }

  const activeCount = vehicleTypes.filter((vt) => vt.active).length;

  return (
    <>
      <Navbar flag={false} brandText={t("Settings")} />
      <Card extra="w-full mt-4 p-6 h-full">
        <header className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">
                {t("Tipos de Veículos do Sistema")}
              </h1>
              <p className="mt-1 text-sm text-gray-400">
                {t("Habilite ou desabilite os tipos de veículos disponíveis na plataforma")}
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-2 border border-white/10">
              <span className="text-2xl font-bold text-cyan-400">{activeCount}</span>
              <span className="text-xs text-gray-400">/ {vehicleTypes.length}<br />ativos</span>
            </div>
          </div>
        </header>

        {vehicleTypes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-gray-400">
              {t("Nenhum tipo de veículo configurado.")}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {t("Configure AVAILABLE_VEHICLE_TYPES no .env do backend.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {vehicleTypes.map((vt) => {
              const colors = TYPE_COLORS[vt.type] || TYPE_COLORS.car;
              const icon = TYPE_ICONS[vt.type] || TYPE_ICONS.car;
              const isToggling = togglingId === vt.id;
              const isExpanded = expandedId === vt.id;

              return (
                <div
                  key={vt.id}
                  onClick={() => !isToggling && handleToggle(vt.id)}
                  className={`relative flex flex-col items-center gap-3 rounded-2xl border p-6 transition-all duration-300 cursor-pointer select-none ${vt.active
                    ? `${colors.border} bg-gradient-to-br from-white/5 to-transparent hover:from-white/8`
                    : "border-white/5 bg-white/[0.02] opacity-60 hover:opacity-80"
                    } ${isToggling ? "pointer-events-none animate-pulse" : ""}`}
                >
                  {/* Active badge */}
                  <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${vt.active
                    ? "bg-green-500/20 text-green-400"
                    : "bg-red-500/20 text-red-400"
                    }`}>
                    {vt.active ? "ACTIVE" : "INACTIVE"}
                  </span>

                  {/* Icon */}
                  <div
                    className={`flex h-16 w-16 items-center justify-center rounded-2xl transition-all ${vt.active ? `${colors.bg} ${colors.text}` : "bg-gray-800/50 text-gray-500"
                      }`}
                  >
                    {icon}
                  </div>

                  {/* Label */}
                  <h3
                    className={`text-sm font-bold ${vt.active ? "text-white" : "text-gray-500"
                      }`}
                  >
                    {vt.label}
                  </h3>

                  {/* Transport Modes UI */}
                  {vt.active && (
                    <div
                      className="flex gap-4 mt-2 mb-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="flex items-center gap-1.5 cursor-pointer disabled:opacity-50 text-xs text-gray-400 hover:text-white transition-colors">
                        <input
                          type="checkbox"
                          className="accent-cyan-500 w-3 h-3 cursor-pointer"
                          checked={(vt.allowedTransportModes ?? []).includes(Types.TransportMode.PERSON)}
                          onChange={() => handleModeToggle(vt.id, vt.allowedTransportModes ?? [], Types.TransportMode.PERSON)}
                        />
                        <span>{Types.getTransportModeEmoji(Types.TransportMode.PERSON)} {Types.getTransportModeLabel(Types.TransportMode.PERSON)}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer disabled:opacity-50 text-xs text-gray-400 hover:text-white transition-colors">
                        <input
                          type="checkbox"
                          className="accent-cyan-500 w-3 h-3 cursor-pointer"
                          checked={(vt.allowedTransportModes ?? []).includes(Types.TransportMode.CARGO)}
                          onChange={() => handleModeToggle(vt.id, vt.allowedTransportModes ?? [], Types.TransportMode.CARGO)}
                        />
                        <span>{Types.getTransportModeEmoji(Types.TransportMode.CARGO)} {Types.getTransportModeLabel(Types.TransportMode.CARGO)}</span>
                      </label>
                    </div>
                  )}

                  {/* Categories count — clickable to expand (stop propagation) */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleExpand(vt.id); }}
                    className={`text-xs px-2 py-1 rounded-lg transition-colors ${isExpanded
                      ? "bg-cyan-500/20 text-cyan-400"
                      : "text-gray-500 hover:text-gray-300 hover:bg-white/5"
                      }`}
                  >
                    {vt.categoriesCount} {vt.categoriesCount === 1 ? "categoria" : "categorias"} {isExpanded ? "▲" : "▼"}
                  </button>


                  {/* Expanded categories list */}
                  {isExpanded && (
                    <div className="w-full mt-1 border-t border-white/10 pt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                      {loadingCategories ? (
                        <p className="text-xs text-gray-500 text-center">{t("Carregando...")}</p>
                      ) : expandedCategories.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center">{t("Nenhuma categoria vinculada")}</p>
                      ) : (
                        expandedCategories.map((cat) => (
                          <div
                            key={cat.id}
                            className={`flex items-center justify-between px-2 py-1.5 rounded-lg transition-all ${cat.active ? 'bg-white/5' : 'bg-white/[0.02] opacity-60'}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`text-xs font-medium truncate ${cat.active ? 'text-white' : 'text-gray-500'}`}>
                                {cat.name}
                              </span>
                              <span
                                className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${cat.transportMode === Types.TransportMode.CARGO
                                  ? "bg-amber-500/20 text-amber-400"
                                  : "bg-cyan-500/20 text-cyan-400"
                                  }`}
                              >
                                {Types.getTransportModeEmoji(cat.transportMode ?? '')}
                              </span>
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Switch
                                isChecked={cat.active}
                                isDisabled={togglingCatId === cat.id}
                                onChange={() => handleToggleCategory(cat.id, cat.active)}
                                colorScheme={cat.active ? "green" : "gray"}
                                size="sm"
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ─── Legacy: Vehicle Make/Model Category Mapping ─────────────── */}
      <VehicleMakeModelTable />
    </>
  );
}

// ─── Original table component (vehicle makes/models → categories) ──────────

type CategoryOption = {
  id: number;
  name: string;
  active: boolean;
  multiply: number;
};

type VehicleMakeRow = {
  vehicleMake: string;
  vehicleModel: string;
  year: string;
  categoryId: number | null;
  categoryName: string;
  categoryActive: boolean;
  categoryMultiply: number;
  vehicleCount: number;
};

const makeColumnHelper = createColumnHelper<VehicleMakeRow>();

function VehicleMakeModelTable() {
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = useState<VehicleMakeRow[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingRow, setUpdatingRow] = useState<string | null>(null);

  const handleCategoryChange = useCallback(
    async (row: VehicleMakeRow, newCategoryId: number) => {
      const rowKey = `${row.vehicleMake}|${row.vehicleModel}|${row.year}`;
      setUpdatingRow(rowKey);
      try {
        await updateVehicleTypeCategory({
          currentCategoryId: row.categoryId ?? null,
          newCategoryId,
        });
        successToast("Categoria atualizada com sucesso!");
        const res = await getVehicleTypeList();
        const payload = res?.data ?? res;
        setData(payload?.data ?? payload ?? []);
      } catch {
        errorToast("Erro ao atualizar categoria");
      } finally {
        setUpdatingRow(null);
      }
    },
    []
  );

  const columns = [
    makeColumnHelper.accessor("vehicleMake", {
      id: "vehicleMake",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Marca")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-white capitalize">{info.getValue() || "-"}</p>
      ),
    }),
    makeColumnHelper.accessor("vehicleModel", {
      id: "vehicleModel",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Modelo")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-white capitalize">{info.getValue() || "-"}</p>
      ),
    }),
    makeColumnHelper.accessor("year", {
      id: "year",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Ano")}</p>,
      cell: (info) => <p className="text-sm font-bold text-white">{info.getValue() || "-"}</p>,
    }),
    makeColumnHelper.accessor("categoryName", {
      id: "categoryName",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Categoria")}</p>,
      cell: (info) => {
        const row = info.row.original;
        const rowKey = `${row.vehicleMake}|${row.vehicleModel}|${row.year}`;
        const isUpdating = updatingRow === rowKey;
        return (
          <div className="relative">
            <select
              className="appearance-none bg-white/5 text-white text-sm font-medium rounded-lg px-3 py-1.5 pr-8 border border-white/10 hover:border-cyan-400/50 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all cursor-pointer disabled:opacity-50"
              value={row.categoryId ?? "-"}
              disabled={isUpdating}
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                if (!isNaN(selectedId) && selectedId > 0) handleCategoryChange(row, selectedId);
              }}
            >
              <option value="-" disabled>Sem categoria</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <FaCaretDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
          </div>
        );
      },
    }),
    makeColumnHelper.accessor("vehicleCount", {
      id: "vehicleCount",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Veículos")}</p>,
      cell: (info) => (
        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-cyan-500/20 text-cyan-400">
          {info.getValue()}
        </span>
      ),
    }),
    makeColumnHelper.accessor("categoryMultiply", {
      id: "categoryMultiply",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Multiplicador")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-white">{Number(info.getValue()).toFixed(2)}x</p>
      ),
    }),
    makeColumnHelper.accessor("categoryActive", {
      id: "categoryActive",
      header: () => <p className="text-sm font-bold text-gray-300">{t("Status")}</p>,
      cell: (info) => {
        const active = info.getValue();
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"
            }`}>
            {active ? "Ativo" : "Inativo"}
          </span>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const [vehiclesRes, categoriesRes] = await Promise.all([
        getVehicleTypeList(),
        getVehicleCategoryList(),
      ]);
      const vehiclesPayload = vehiclesRes?.data ?? vehiclesRes;
      setData(vehiclesPayload?.data ?? vehiclesPayload ?? []);
      const categoriesPayload = categoriesRes?.data ?? categoriesRes;
      setCategories(categoriesPayload?.data ?? categoriesPayload ?? []);
    } catch {
      errorToast(t("Alguma coisa deu errado!"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { getData(); }, [getData]);

  return (
    <Card extra="w-full mt-4 pb-10 p-4 h-full">
      <header className="relative flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-white">{t("Mapeamento de Veículos")}</h2>
          <p className="mt-1 text-sm text-gray-400">
            {t("Associe marcas e modelos cadastrados a categorias de tarifa")}
          </p>
        </div>
      </header>

      {loading ? (
        <Loader />
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-white/10">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer pb-3 pr-4 pt-4 text-start"
                    >
                      <div className="flex gap-2 items-center text-left text-xs text-gray-300">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === "asc" ? (
                          <FaCaretUp className="text-cyan-400" size={14} />
                        ) : header.column.getIsSorted() === "desc" ? (
                          <FaCaretDown className="text-cyan-400" size={14} />
                        ) : (
                          <FaCaretDown className="text-gray-600" size={14} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {data.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className="text-center py-12">
                    <p className="text-lg text-gray-400">{t("Nenhum tipo de veículo cadastrado")}</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`border-b border-white/5 transition-colors hover:bg-white/[0.03] ${idx % 2 === 1 ? "bg-white/[0.01]" : ""
                      }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="min-w-[100px] py-3 pr-4 text-start">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}
    </Card>
  );
}

export default VehicleTypeList;
